/* RESPONSIBLE TEAM: team-phone */
import StepConfig, { type StepConfigParams } from '../step';
import type FinCustom from 'embercom/models/operator/visual-builder/step/fin-custom';
import AttributeInfoResolver from 'embercom/lib/common/template-attribute-resolver';
import type Store from '@ember-data/store';
import type InsertableAction from 'embercom/models/workflow-connector/insertable-action';
import { Type as AttributeDescriptorType } from 'embercom/models/operator/visual-builder/attribute-descriptor';
import CommandsResolver from 'embercom/lib/workflows/commands-resolver';
import type WorkflowConnectorInsertableActionsService from 'embercom/services/workflow-connector-insertable-actions';

export default class FinCustomConfiguration extends StepConfig {
  declare step: FinCustom;
  protected attributeService: any;
  store: Store;
  workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
  app: any;

  constructor(
    params: Omit<StepConfigParams, 'component'> & {
      component?: string;
      app: any;
      attributeService: any;
      store: Store;
      workflowConnectorInsertableActions: WorkflowConnectorInsertableActionsService;
    },
  ) {
    super({
      component: 'workflows/graph-editor/node-items/steps/fin-custom',
      ...params,
    });
    this.app = params.app;
    this.attributeService = params.attributeService;
    this.store = params.store;
    this.workflowConnectorInsertableActions = params.workflowConnectorInsertableActions;
  }

  localAttributeGroupListExcludingOutputParams() {
    let localAttributeGroupList = this.step.editorConfig?.localAttributeGroupList;
    let localVariables = localAttributeGroupList?.find((group) => {
      return (
        group.heading ===
        this.intl.t('operator.workflows.visual-builder.attribute-collector.local-variables')
      );
    });

    if (localVariables) {
      localVariables.attributes = localVariables.attributes.filter(
        (attribute: any) => !this.step.finOutputParams?.toArray().includes(attribute),
      );
    }

    return localAttributeGroupList;
  }

  deleteStep() {
    this.step.finOutputParams.toArray().forEach((attribute) => {
      this.editorState.deleteLocalVariable(attribute);
    });
    super.deleteStep();
  }

  get alignment() {
    return 'left' as const;
  }

  get attributeInfoResolveForUpdates() {
    return new AttributeInfoResolver({
      attributes: this.instructionsCollectableAttributes(),
      localAttributes: this.instructionsCollectableLocalAttributesGroupList(),
      allLocalAttributes: this.step.editorConfig?.allLocalAttributeGroupList,
    });
  }

  get attributeInfoResolver() {
    return new AttributeInfoResolver({
      attributes:
        this.step.editorConfig?.runtimeMatchingTargetingAttributes
          .map((group) => group.attributes)
          .flat() ?? this.attributeService.userEditorMetadataAndConversationAttributes,
      customObjectAttributes: this.attributeService.customObjectsAttributeDescriptors,
      includeAppAttributes: false,
      includeCustomObjectAttributes: true,
      includeFileAttributes: this.step.appService.app.canUseFinCustomImageRecognition,
      localAttributes: this.step.isV3
        ? this.step.editorConfig?.localAttributeGroupList
        : this.localAttributeGroupListExcludingOutputParams(),
      allLocalAttributes: this.step.editorConfig?.allLocalAttributeGroupList,
      customActions: this.finCustomActionsGroupList(),
      showCategoryPrefixes: true,
    });
  }

  get commandsResolver() {
    let teamHeader = this.intl.t(
      'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.team-header',
    );
    let teammateHeader = this.intl.t(
      'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.teammates-header',
    );
    let defaultTeamHeader = this.intl.t(
      'operator.workflows.visual-builder.fin-custom-settings.inline-attribute.default-team-header',
    );
    return new CommandsResolver({
      tags: this.tags,
      actions: this.finCustomActionsGroupList(),
      teammates: {
        list: this.app.assignableHumanAdmins,
        header: teammateHeader,
      },
      teams: {
        list: this.app.assignableTeams,
        header: teamHeader,
      },
      defaultTeamHeader,
    });
  }

  get tags() {
    return this.store.peekAll('tag');
  }

  finCustomActionsGroupList() {
    let actionsHeading = this.intl.t(
      'operator.workflows.visual-builder.fin-custom-settings.configure.data-connectors',
    );
    return [
      {
        heading: actionsHeading,
        attributes: this.workflowConnectorInsertableActions.actions
          .filter((action: InsertableAction) => action.state === 'live' && !action.isTemplate)
          .map((action: InsertableAction) => {
            return {
              identifier: `workflow_connector.${action.id}`,
              templatable_identifier: `workflow_connector.${action.id}`,
              composer_name: this.step.isV3
                ? action.name
                : this.intl.t(
                    'operator.workflows.visual-builder.fin-custom-settings.configure.prompt.custom-action',
                    {
                      action_name: action.name,
                    },
                  ),
              name: action.name,
              type: 'string',
              icon: 'webhook',
              noFallback: true,
            };
          }),
      },
    ];
  }

  private instructionsCollectableAttributes() {
    let excludedAttributeIdentifier = this.app
      .canUseFinProceduresAllowEmailForLeadsWithoutIdentifiers
      ? ['phone']
      : ['phone', 'email'];

    return this.attributeService.instructionsCollectableAttributeGroupList
      .flatMap((group: $TSFixMe) => group.attributes)
      .filter(
        (attribute: $TSFixMe) =>
          !(
            excludedAttributeIdentifier.includes(attribute.identifier) ||
            attribute?.type === 'files'
          ),
      );
  }

  private instructionsCollectableLocalAttributesGroupList() {
    return this.step.editorConfig?.collectableLocalAttributesGroupList.map((attributeGroup) => {
      return {
        heading: attributeGroup.heading,
        attributes: attributeGroup.attributes.filter(
          (attribute: $TSFixMe) => attribute.type !== AttributeDescriptorType.array,
        ),
      };
    });
  }
}
